import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../../utils/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { formatCategory } from '../../utils/formatters';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const PersonalProjects = () => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll animations
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: projectsRef, isVisible: projectsVisible } = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await projectsAPI.getBySlug('nature-macro-photography');
        setProject(response.data.data);
      } catch (err) {
        setError(err.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header with fade-in */}
          <div
            ref={headerRef}
            className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
              Personal Projects
            </h2>
            <p className="text-lg md:text-xl text-text-secondary font-body leading-relaxed">
              Exploring creativity beyond client work
            </p>
          </div>

          {/* Projects with stagger animation */}
          <div ref={projectsRef} className="space-y-12">
            {loading && (
              <div className="flex justify-center">
                <Loading size="lg" text="Loading project..." />
              </div>
            )}
            {error && <ErrorMessage message={error} />}
            {project && (
              <div
                className={`transition-all duration-700 ease-out ${
                  projectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
              >
                <Link to={`/portfolio/${project.slug}`}>
                  <Card hover>
                    <div className="aspect-w-3 aspect-h-4 relative overflow-hidden">
                      <img
                        src={project.coverImage}
                        alt={project.title}
                        className="w-full object-cover transition-transform duration-600 hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 lg:p-8">
                      <div className="text-sm text-accent-secondary font-body font-semibold uppercase tracking-wider mb-2">
                        {formatCategory(project.category)}
                      </div>
                      <h3 className="text-xl lg:text-2xl font-heading font-bold text-secondary mb-3 group-hover:text-accent-secondary transition-colors duration-400">
                        {project.title}
                      </h3>
                      <p className="text-text-secondary font-body leading-relaxed line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                  </Card>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalProjects;
